import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, RefreshCw, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { ADMIN } from '@/constants/testIds';

const API = '/api';
function getHeaders() { const t = localStorage.getItem('pkepl_token'); return t ? { Authorization: `Bearer ${t}` } : {}; }

const STATUS_OPTIONS = ['new', 'read', 'replied', 'archived'];
const STATUS_STYLES = {
  new: 'bg-blue-50 text-blue-700 border-blue-200',
  read: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  replied: 'bg-green-50 text-green-700 border-green-200',
  archived: 'bg-gray-100 text-gray-600 border-gray-200',
};

function formatDate(d) { return d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'; }

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [resending, setResending] = useState(false);

  const fetchInquiries = () => {
    setLoading(true);
    const params = filter !== 'all' ? `?status=${filter}` : '';
    axios.get(`${API}/admin/inquiries${params}`, { withCredentials: true, headers: getHeaders() })
      .then(r => {
        const data = r.data;
        setInquiries(Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []));
      }).catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchInquiries(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/inquiries/${id}`, { status }, { withCredentials: true, headers: getHeaders() });
      toast.success('Status updated'); fetchInquiries();
      if (selected?.id === id) setSelected(prev => ({ ...prev, status }));
    } catch { toast.error('Failed to update'); }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, { withCredentials: true, headers: getHeaders() });
      toast.success('Deleted'); fetchInquiries();
      if (selected?.id === id) setSelected(null);
    } catch { toast.error('Failed to delete'); }
  };

  const resendNotification = async (id) => {
    setResending(true);
    try {
      await axios.post(`${API}/admin/inquiries/${id}/resend-notification`, {}, { withCredentials: true, headers: getHeaders() });
      toast.success('Notification email sent');
      fetchInquiries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send. Check RESEND_API_KEY in Website Settings.');
    } finally { setResending(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold text-[#153E5C]">Inquiries</h1>
          <p className="text-sm text-[#6B7280] font-sans mt-1">Manage contact form submissions and inquiries.</p>
        </div>
        <button onClick={fetchInquiries} className="p-2 rounded-lg border border-[#E5E7EB] text-[#6B7280] hover:text-[#0E5A57] hover:bg-teal-50 transition-colors duration-150">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {['all', ...STATUS_OPTIONS].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-4 py-2 rounded-full text-sm font-medium flex-shrink-0 transition-colors duration-150 capitalize ${filter === s ? 'bg-[#0E5A57] text-white' : 'bg-white border border-[#E5E7EB] text-[#374151] hover:bg-teal-50 hover:text-[#0E5A57]'}`}>
            {s}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* List */}
        <div className="lg:col-span-3">
          <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden" data-testid={ADMIN.inquiriesList}>
            {loading ? (
              <div className="p-8 text-center"><div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
            ) : inquiries.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#6B7280] font-sans">No inquiries found.</div>
            ) : (
              <div className="divide-y divide-[#F3F4F6]">
                {inquiries.map(inq => (
                  <div key={inq.id} onClick={() => setSelected(inq)}
                    className={`p-4 cursor-pointer hover:bg-[#F9FAFB] transition-colors duration-150 ${selected?.id === inq.id ? 'bg-teal-50 border-l-2 border-[#0E5A57]' : ''}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-[#1F2937] font-sans truncate">{inq.name}</p>
                        <p className="text-xs text-[#9CA3AF] font-sans truncate">{inq.email}</p>
                        {inq.service && <p className="text-xs text-[#6B7280] mt-1 font-sans">{inq.service}</p>}
                      </div>
                      <div className="flex-shrink-0 flex flex-col items-end gap-1">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLES[inq.status] || 'bg-gray-50 border-gray-200'}`}>{inq.status}</span>
                        <div className="flex items-center gap-1">
                          {inq.email_sent ? (
                            <CheckCircle className="w-3 h-3 text-green-500" title="Email notification sent" />
                          ) : (
                            <AlertCircle className="w-3 h-3 text-amber-400" title="Email notification not sent" />
                          )}
                          <span className="text-xs text-[#9CA3AF] font-sans">{formatDate(inq.created_at).split(',')[0]}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {selected ? (
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 space-y-5 sticky top-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-serif text-lg font-bold text-[#153E5C]">{selected.name}</h3>
                  <a href={`mailto:${selected.email}`} className="text-sm text-[#0E5A57] font-sans">{selected.email}</a>
                </div>
                <button onClick={() => deleteInquiry(selected.id)} className="p-2 text-[#9CA3AF] hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors duration-150">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3 text-sm font-sans">
                {selected.phone && <div><span className="text-xs overline-label text-[#9CA3AF] block mb-0.5">Phone</span><span className="text-[#374151]">{selected.phone}</span></div>}
                {selected.company && <div><span className="text-xs overline-label text-[#9CA3AF] block mb-0.5">Company</span><span className="text-[#374151]">{selected.company}</span></div>}
                {selected.country && <div><span className="text-xs overline-label text-[#9CA3AF] block mb-0.5">Country</span><span className="text-[#374151]">{selected.country}</span></div>}
                {selected.service && <div><span className="text-xs overline-label text-[#9CA3AF] block mb-0.5">Service</span><span className="text-[#374151]">{selected.service}</span></div>}
                <div><span className="text-xs overline-label text-[#9CA3AF] block mb-0.5">Received</span><span className="text-[#374151]">{formatDate(selected.created_at)}</span></div>
                <div className="flex items-center gap-2">
                  <span className="text-xs overline-label text-[#9CA3AF]">Email Notification:</span>
                  {selected.email_sent ? (
                    <span className="flex items-center gap-1 text-xs text-green-600 font-medium"><CheckCircle className="w-3 h-3" /> Sent</span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-amber-600 font-medium"><AlertCircle className="w-3 h-3" /> Not sent</span>
                  )}
                </div>
              </div>

              <div>
                <span className="text-xs overline-label text-[#9CA3AF] block mb-2">Message</span>
                <div className="bg-[#F9FAFB] rounded-lg p-4 text-sm text-[#374151] font-sans leading-relaxed border border-[#E5E7EB]">{selected.message}</div>
              </div>

              <div>
                <span className="text-xs overline-label text-[#9CA3AF] block mb-2">Update Status</span>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map(s => (
                    <button key={s} onClick={() => updateStatus(selected.id, s)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border capitalize transition-colors duration-150 ${selected.status === s ? STATUS_STYLES[s] : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:bg-[#F3F4F6]'}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <a href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.service || 'Your Inquiry')} - NKP VENTURES`}
                  className="block w-full text-center bg-[#0E5A57] hover:bg-[#2E7D7A] text-white text-sm font-medium py-2.5 rounded-xl transition-colors duration-200 font-sans">
                  Reply via Email
                </a>
                <button onClick={() => resendNotification(selected.id)} disabled={resending}
                  data-testid="resend-notification-btn"
                  className="flex items-center justify-center gap-2 w-full text-center border border-[#E5E7EB] text-[#374151] hover:bg-[#F7FAFC] disabled:opacity-60 text-sm font-medium py-2.5 rounded-xl transition-colors duration-200 font-sans">
                  <Mail className="w-4 h-4" />
                  {resending ? 'Sending...' : 'Resend Notification'}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-8 text-center">
              <p className="text-sm text-[#9CA3AF] font-sans">Select an inquiry to view details.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
